import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import withStyles from 'react-jss';
import { Button, Input, Select, Row, Space, DatePicker } from 'antd';
import { useHistory } from 'react-router';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { PlusOutlined } from '@ant-design/icons';
import moment from 'moment';

import GetField from '../Components/GetField';
import styles from '../styles';
import { successNotification, errorNotification } from '../Snackbar';
import Api from '../Api';
import LeftGrid from '../Components/LeftGrid';
import RightGrid from '../Components/RightGrid';
import {
  clearCustomers,
  clearProductIds,
  clearStylesForJobchart,
  getCustomers,
  getProductIds,
  getStylesForJobchart,
  getColorIds,
  getFabricStocks,
  clearColorIds,
  clearFabricStocks,
  getJobchart,
  clearJobchart,
  clearJobchartFiles,
} from '../Actions';
import Breadcrumbs from '../Components/Breadcrumbs';
import ProductForm from './ProductForm';
import { get } from '../utils';
import SummaryModal from './Summary';

const AntInput = GetField(Input);
const AntTextArea = GetField(Input.TextArea);
const AntSelect = GetField(Select);
const AntDatePicker = GetField(DatePicker);
const { Option } = Select;

const UpdateJobchart = props => {
  const { classes, match } = props;

  const history = useHistory();
  const dispatch = useDispatch();

  const auth = useSelector(state => state.auth);
  const role = useMemo(() => get(auth, ['user', 'role'], 'Owner'), [auth]);

  const [confirmLoading, setConfirmLoading] = useState(false);
  const [summary, setSummary] = useState(false);

  const filesState = useSelector(state => state.jobchartFiles);
  const jobchart = useSelector(state => state.jobchart);
  const fabricStocks = useSelector(state => state.fabricStocks);
  const accessoryStocks = useSelector(state => state.accessoryStocks);

  const {
    handleSubmit,
    control,
    getValues,
    setValue,
    watch,
    reset,
    unregister,
  } = useForm({
    defaultValues: {
      products: [
        {
          productId: null,
          isAll: false,
          styles: [
            {
              fabrics: [{ fabricId: null }],
              accessories: [{ accessoryId: null }],
            },
          ],
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'products',
  });

  const customers = useSelector(state => state.customers);

  useEffect(() => {
    dispatch(getCustomers());
    dispatch(getColorIds());
    dispatch(getFabricStocks());
    dispatch(getJobchart(match.params.id));
    return () => {
      dispatch(clearCustomers());
      dispatch(clearProductIds());
      dispatch(clearColorIds());
      dispatch(clearFabricStocks());
      dispatch(clearJobchart());
      dispatch(clearJobchartFiles());
    };
  }, []);

  useEffect(() => {
    if (Object.keys(jobchart).length) {
      const { customerId, bpoNo, shipDate, jobDate, remark, products } =
        jobchart;

      reset({
        customerId,
        bpoNo,
        shipDate: shipDate || null,
        jobDate: jobDate || null,
        remark,
        products,
      });
    }
  }, [jobchart]);

  useEffect(() => {
    const customerId = getValues('customerId');
    if (!customerId) {
      dispatch(clearStylesForJobchart());
      dispatch(clearProductIds());
    } else {
      dispatch(getProductIds(customerId));
      dispatch(getStylesForJobchart(customerId));
    }
  }, [watch('customerId')]);

  const calculateStocks = () => {
    const products = getValues('products');
    const _products = JSON.parse(JSON.stringify(products));

    _products.map(product => {
      (product.styles || []).map(style => {
        style.quantity = +style.quantity || 0;
        style.additional = +style.additional || 0;

        style.totalQuantity = style.quantity + style.additional;

        (style.fabrics || []).map(fabric => {
          const fabricId = fabric.fabricId || '';
          const productId = product.productId || '';
          const baseColorId = fabric.baseColorId || '';
          const printColorId = fabric.printColorId || '';

          fabric.consumption = +fabric.consumption || 0;
          fabric.additionalPercentage = +fabric.additionalPercentage || 0;

          fabric.quantity = +(fabric.consumption * style.totalQuantity).toFixed(
            2
          );

          fabric.totalQuantity = +(
            fabric.quantity *
            (1 + +fabric.additionalPercentage / 100)
          ).toFixed(2);

          const stock =
            fabricStocks[
              `P${productId}F${fabricId}B${baseColorId}P${printColorId}`
            ];

          fabric.stock = stock ? +stock.stock : 0;

          fabric.toOrder =
            fabric.stock > fabric.totalQuantity
              ? 0
              : +(fabric.totalQuantity - fabric.stock).toFixed(2);
        });

        (style.accessories || []).map(accessory => {
          const accessoryId = accessory.accessoryId || '';
          const productId = product.productId || '';

          accessory.consumption = +accessory.consumption || 0;
          accessory.additionalPercentage = +accessory.additionalPercentage || 0;

          accessory.quantity = +(
            accessory.consumption * style.totalQuantity
          ).toFixed(2);

          accessory.totalQuantity = +(
            accessory.quantity *
            (1 + +accessory.additionalPercentage / 100)
          ).toFixed(2);

          const stock = accessoryStocks[`P${productId}A${accessoryId}`];

          accessory.stock = stock ? +stock.stock : 0;

          accessory.toOrder =
            accessory.stock > accessory.totalQuantity
              ? 0
              : +(accessory.totalQuantity - accessory.stock).toFixed(2);
        });
      });
    });

    setValue('products', _products);
  };

  const onSubmit = useCallback(async formValues => {
    try {
      setConfirmLoading(true);
      const formData = new FormData();

      const { productIndexes } = filesState;

      formData.append(
        'data',
        JSON.stringify({
          ...formValues,
          shipDate: formValues.shipDate
            ? moment(formValues.shipDate).toISOString()
            : null,
          jobDate: formValues.jobDate
            ? moment(formValues.jobDate).toISOString()
            : null,
          productIndexes,
        })
      );

      let index = 0;

      for (const key of Object.keys(filesState.products)) {
        if (filesState.products[key] && filesState.products[key].files.length) {
          for (const file of filesState.products[key].files) {
            formData.append(`files[${index}]`, file);
            index += 1;
          }
        }
      }

      await Api.put(`/jobcharts/${match.params.id}`, formData, {
        headers: { 'content-type': 'multipart/form-data' },
      });

      successNotification('Jobchart updated');
      history.push('/jobcharts/list');
    } catch (err) {
      setConfirmLoading(false);
      errorNotification(err);
    }
  }, []);

  const productAppend = useCallback(() => {
    append({ isAll: false, styles: [{ fabrics: [{}], accessories: [{}] }] });
  }, [append]);

  const viewSummary = () => setSummary(true);

  return (
    <>
      <Breadcrumbs
        moduleName="Jobcharts"
        moduleLink="/jobcharts/list"
        subModuleName="Update"
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <Row>
          <LeftGrid>
            <Controller
              name="customerId"
              control={control}
              render={({ field }) => (
                <AntSelect
                  allowClear
                  label="Customer"
                  placeholder="Select customer"
                  {...field}
                >
                  {customers.map(customer => (
                    <Option key={customer.id} value={customer.id}>
                      {customer.name}
                    </Option>
                  ))}
                </AntSelect>
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="bpoNo"
              control={control}
              render={({ field }) => (
                <AntInput
                  allowClear
                  label="BPO #"
                  placeholder="BPO #"
                  {...field}
                />
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="shipDate"
              control={control}
              render={({ field }) => (
                <AntDatePicker
                  disabled={role !== 'Owner'}
                  label="Ship Date"
                  placeholder="Ship Date"
                  {...field}
                />
              )}
            />
          </LeftGrid>
          <RightGrid>
            <Controller
              name="jobDate"
              control={control}
              render={({ field }) => (
                <AntDatePicker
                  disabled={role !== 'Owner'}
                  label="BPO Date"
                  placeholder="BPO Date"
                  {...field}
                />
              )}
            />
          </RightGrid>
        </Row>
        <Row>
          <LeftGrid>
            <Controller
              name="remark"
              control={control}
              render={({ field }) => (
                <AntTextArea
                  allowClear
                  label="Remark"
                  placeholder="Remark"
                  {...field}
                />
              )}
            />
          </LeftGrid>
        </Row>
        <Row style={{ padding: 30 }}>
          <Space direction="vertical" style={{ width: '100%' }}>
            {fields.map((item, index) => (
              <ProductForm
                key={item.id}
                productItem={item}
                productIndex={index}
                productRemove={remove}
                control={control}
                getValues={getValues}
                setValue={setValue}
                watch={watch}
                unregister={unregister}
              />
            ))}
          </Space>
        </Row>

        <Row className={classes.buttonContainer}>
          <Button type="primary" htmlType="button" onClick={productAppend}>
            <PlusOutlined /> Product
          </Button>
          &nbsp; &nbsp;
          <Button type="primary" htmlType="button" onClick={calculateStocks}>
            Calculate
          </Button>
          &nbsp; &nbsp;
          <Button type="primary" htmlType="button" onClick={viewSummary}>
            Summary
          </Button>
          &nbsp; &nbsp;
          <Button type="primary" loading={confirmLoading} htmlType="submit">
            Submit
          </Button>
        </Row>
      </form>
      {summary && <SummaryModal getValues={getValues} setModal={setSummary} />}
    </>
  );
};

UpdateJobchart.propTypes = {
  classes: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
};

export default withStyles(styles)(UpdateJobchart);
